import { useDraggable } from '@dnd-kit/react'
import type { TestGroupingFontSchemaType, TestGroupingSchemaType } from '@vedalib/editor/lib/brand'
import {
  FeedbackMode,
  SectionTypeEnum,
  type TestGroupingGroup,
  type TestGroupingItem,
} from '@vedalib/editor/lib/elements'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { textToRtValue } from '@vedalib/rich-text'
import cn from 'classnames'
import { motion } from 'framer-motion'
import { useContext } from 'use-context-selector'

import { getObjectFitDivMap } from 'components/Editor/EditorElements/utils/getObjectFitDivMap'
import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import RichText from 'components/form/RichText'
import { RichTextViewer } from 'components/form/RichText/RichTextViewer'
import { IconButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import { KitSize } from 'components/uiKit/KitTypes'
import type { ElementFontCss, ElementStyleCss } from 'services/Branding/types'
import { AppMode, PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getSection } from 'services/Store/Project/selectors'
import { t } from 'services/Translation'
import { useUrlByUsage } from 'utils/files'

import TestGroupingContext from '../../TestGroupingContext'
import { borderRadiusContent, createStackDraggableId, getGroupName } from '../../helper'
import FeedbackIcon from '../FeedbackIcon'
import * as s from './StackCard.module.scss'

interface IStackCardProps {
  type: 'answer' | 'group'
  group: TestGroupingGroup
  item?: TestGroupingItem
  relativePosition: number
  isActive: boolean
  styles: ElementStyleCss<TestGroupingSchemaType>
  font: ElementFontCss<TestGroupingFontSchemaType>
  ariaDescribedBy?: string
}

const StackCard = ({
  type,
  group,
  item,
  relativePosition,
  isActive,
  styles,
  font,
  ariaDescribedBy,
}: IStackCardProps) => {
  const {
    selectedGroupId,
    mode,
    onAddGroup,
    setSelectedGroupId,
    setSelectedAnswerId,
    isActiveElement,
    activeIndex,
    onLabelSelect,
    onChangeLabel,
    isFill: contextIsFill,
    destination,
    testResult,
    blockResult,
    feedback,
    value,
    state,
    setState,
  } = useContext(TestGroupingContext)
  const section = useProjectContext(getSection)
  const feedbackMode = section?.test?.feedbackMode
  const showFbIcon =
    section?.type === SectionTypeEnum.landing ||
    (section?.type === SectionTypeEnum.test &&
      feedbackMode !== FeedbackMode.HIDE_ALL &&
      feedbackMode !== FeedbackMode.HIDE_ICON)
  const items = value.items
  const isSelected = selectedGroupId === group.value
  const isEditor = mode.previewMode === PreviewMode.editor
  const isFill = isEditor && mode.editorMode === AppMode.fill
  const isGroupCard = type === 'group'
  const hasGroupImage = !!group.target.image?.id

  const { ref: draggableRef, isDragging } = useDraggable({
    id: item ? createStackDraggableId(group.value, item.value) : `non-draggable-${group.value}`,
    data: item ?? group,
    disabled: isGroupCard || !item || !!blockResult,
  })

  const handleCardClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isGroupCard) {
      setSelectedAnswerId(undefined)
      setSelectedGroupId(isSelected ? undefined : group.value)
      setState?.({ ...state, activeGroup: group.value })
    }
  }

  const groupImageUrl = useUrlByUsage(group.target.image, FileUsageTypeEnum.image)
  const answerImageUrl = useUrlByUsage(item?.image ?? null, FileUsageTypeEnum.image)

  const imageUrl = isGroupCard ? groupImageUrl : answerImageUrl

  const absPosition = Math.abs(relativePosition)
  const maxVisibleCards = 4
  const isVisible = absPosition < maxVisibleCards

  const visiblePosition = Math.min(absPosition, maxVisibleCards - 1)
  const baseOffset = blockResult ? 20 : 16
  const stackOffset = visiblePosition * baseOffset

  const getStackValue = (base: number, factor: number, min: number) => {
    if (isDragging || isActive) {
      return base
    }
    if (isVisible) {
      return Math.max(base - absPosition * factor, min)
    }
    return min
  }

  const scale = getStackValue(1, 0.08, 0.7)
  const opacity = getStackValue(1, 0.25, 0)
  const zIndex = isDragging ? 9999 : isActive ? 100 : 100 - absPosition

  const cardStyles = { ...(isGroupCard ? styles.group : styles.answer), ...styles.common }

  const borderRadius = isGroupCard
    ? borderRadiusContent(styles.group) || styles.common.borderRadius
    : borderRadiusContent(styles.answer) || styles.common.borderRadius

  const overlay = isGroupCard ? group.target.overlay : item?.overlay
  const label = isGroupCard ? group.target.label : item?.label
  const objectFit = isGroupCard ? group.target.image?.objectFit : item?.image?.objectFit

  const groupAriaLabel = isGroupCard
    ? [
        getGroupName(group),
        t('elements.grouping.accessibility.answersCount', { count: group.items?.length || 0 }),
      ]
        .filter(Boolean)
        .join(', ')
    : undefined

  const actualGroupIndex = items.findIndex((g) => g.value === group.value)
  const actualAnswerIndex = item
    ? (group.items?.findIndex((a) => a.value === item.value) ?? -1)
    : -1

  const labelName = isGroupCard
    ? `items.${actualGroupIndex}.target.label`
    : `items.${actualGroupIndex}.items.${actualAnswerIndex}.label`
  const labelPath = isGroupCard
    ? ['items', actualGroupIndex, 'target', 'label']
    : ['items', actualGroupIndex, 'items', actualAnswerIndex, 'label']

  const handleAddBefore = (e: React.MouseEvent) => {
    e.stopPropagation()
    onAddGroup(group.value, 'before')
  }

  const handleAddAfter = (e: React.MouseEvent) => {
    e.stopPropagation()
    onAddGroup(group.value, 'after')
  }

  const child = (
    <>
      {isSelected && isGroupCard && isFill && destination.length < 10 && (
        <>
          <AbsolutePortal
            name={`addGroupBefore-${group.value}`}
            placement='left'
            recalculatingKey={destination.length}
            translateX='-50%'
            translateY='-50%'
          >
            <div className={s.actionButton}>
              <Tooltip overlay={t('elements.grouping.tooltips.addGroup')}>
                <IconButton
                  icon='otherAdd'
                  name='addGroupBefore'
                  onClick={handleAddBefore}
                  size={KitSize.XS}
                  inheritColor
                  round
                />
              </Tooltip>
            </div>
          </AbsolutePortal>
          <AbsolutePortal
            name={`addGroupAfter-${group.value}`}
            placement='right'
            recalculatingKey={destination.length}
            translateX='-50%'
            translateY='-50%'
          >
            <div className={s.actionButton}>
              <Tooltip overlay={t('elements.grouping.tooltips.addGroup')}>
                <IconButton
                  icon='otherAdd'
                  name='addGroupAfter'
                  onClick={handleAddAfter}
                  size={KitSize.XS}
                  inheritColor
                  round
                />
              </Tooltip>
            </div>
          </AbsolutePortal>
        </>
      )}
      <div
        aria-describedby={ariaDescribedBy}
        aria-hidden={!isActive}
        aria-label={groupAriaLabel}
        className={s.stackCard}
        ref={isGroupCard ? undefined : draggableRef}
        tabIndex={isActive ? 0 : -1}
      >
        <motion.div
          animate={
            isDragging
              ? false
              : {
                  y: -stackOffset,
                  scale: scale,
                  opacity: opacity,
                }
          }
          className={cn(s.stackItem, {
            [s.selected]: isSelected && isGroupCard && isFill,
          })}
          initial={
            isGroupCard || isDragging
              ? false
              : {
                  scale: 0,
                  opacity: 0,
                  y: 0,
                }
          }
          onClick={handleCardClick}
          style={{
            ...cardStyles,
            backgroundImage:
              (isGroupCard && hasGroupImage && imageUrl) || (!isGroupCard && imageUrl)
                ? `url(${imageUrl})`
                : undefined,
            backgroundSize: getObjectFitDivMap(objectFit),
            cursor: !isGroupCard ? (isDragging ? 'grabbing' : 'grab') : 'default',
            zIndex: isDragging ? 9999 : zIndex,
            backgroundColor: isGroupCard
              ? styles.group.backgroundColor
              : styles.answer.backgroundColor,
            width: styles.common.width,
            height: styles.common.height,

            ...(isDragging && {
              opacity: 1,
              transform: 'scale(1)',
            }),
          }}
          transition={{
            type: 'spring',
            stiffness: 120,
            damping: 20,
            mass: 1,
          }}
          whileHover={
            !isGroupCard && !isDragging && relativePosition !== 0
              ? {
                  y: -stackOffset - 4,
                }
              : undefined
          }
        >
          <div
            className={s.itemMask}
            style={{
              borderRadius,
              backgroundColor: overlay || '',
            }}
          />
          <div
            className={s.itemLabel}
            style={{
              justifyContent: isGroupCard ? styles.group.alignItems : styles.answer.alignItems,
            }}
          >
            <div
              className={s.textInner}
              onMouseDown={(e) => contextIsFill && onLabelSelect(labelName, e)}
              style={font?.base}
            >
              {contextIsFill ? (
                <RichText
                  active={isActiveElement && activeIndex === labelName}
                  disabled={!contextIsFill}
                  isPreview={mode?.previewMode !== PreviewMode.editor}
                  name={labelName}
                  onChange={(val) => onChangeLabel(val, labelPath)}
                  placeholder={
                    isGroupCard
                      ? t('elements.grouping.tags.group')
                      : t('elements.grouping.tags.answer')
                  }
                  styles={font}
                  value={label || textToRtValue('')}
                />
              ) : (
                <RichTextViewer styles={font} value={label || textToRtValue('')} />
              )}
            </div>
          </div>
          {isSelected && isGroupCard && isFill && (
            <div className={s.badge}>
              {t('elements.grouping.tags.group')} {actualGroupIndex + 1}
            </div>
          )}
          {!isGroupCard &&
            item &&
            blockResult &&
            value.answerFeedback &&
            feedback &&
            showFbIcon && (
              <FeedbackIcon feedback={feedback} isTrueAnswer={testResult[item.value]} />
            )}
        </motion.div>
      </div>
    </>
  )

  return child
}

export default StackCard
